import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS, Wordmark } from "@/lib/theme";
import { apiGet } from "@/lib/api";
import { englishDate, hebrewDate } from "@/lib/dates";

import { isPhoneApp } from "@/lib/phoneApp";

// Neutral sponsor placeholders — easy to replace later via admin/content settings.
const SPONSOR_PLACEHOLDERS = [
  { id: "sp1", name: "Your Business Here" },
  { id: "sp2", name: "Community Partner" },
  { id: "sp3", name: "Local Sponsor" },
  { id: "sp4", name: "Kosher Kitchen Co." },
];

const SectionTitle = ({ children, eyebrow }) => (
  <div style={{ margin: "26px 0 12px" }}>
    {eyebrow && (
      <div style={{ color: COLORS.purple, fontWeight: 800, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase" }}>
        {eyebrow}
      </div>
    )}
    <div className="font-heading ls-section-title" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink, lineHeight: 1.15, marginTop: 4 }}>
      {children}
    </div>
  </div>
);

export default function Home({ member, onNav, onOpenDetail, onOpenAI }) {
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [community, setCommunity] = useState([]);
  const [next, setNext] = useState(isPhoneApp ? null : { name: "Mincha", time: "17:15" });

  useEffect(() => {
    apiGet("/events").then((d) => setEvents((d.events || []).filter((event) => !isPhoneApp || Date.parse(event.date_iso) >= Date.now()))).catch(() => {});
    apiGet("/announcements").then((d) => setAnnouncements(d.announcements || [])).catch(() => {});
    apiGet("/community").then((d) => setCommunity(d.sections || [])).catch(() => {});
    apiGet(isPhoneApp ? "/services?phone=true" : "/services").then((d) => {
      const now = new Date();
      const hh = now.getHours() + now.getMinutes() / 60;
      const list = isPhoneApp
        ? [...(now.getDay() === 6 ? d.shabbos : d.weekday)].sort((a, b) => a.time.localeCompare(b.time))
        : d.weekday || [];
      const parseHM = (s) => {
        const m = s.match(/(\d{2}):(\d{2})/);
        return m ? parseInt(m[1], 10) + parseInt(m[2], 10) / 60 : 99;
      };
      const upcoming = list.find((s) => parseHM(s.time) > hh) || list[0];
      if (upcoming) setNext(upcoming);
    }).catch(() => {});
  }, []);

  const today = englishDate();
  const todayHebrew = hebrewDate();

  const AnnouncementsBlock = announcements.length > 0 ? (
    <div>
      <SectionTitle eyebrow="Community">Latest Announcements</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {announcements.slice(0, 3).map((a) => (
          <div
            key={a.id}
            className="ls-card"
            data-testid={`home-announcement-${a.id}`}
            onClick={() => onOpenDetail({ title: "Announcements", body: "announcements" })}
            style={{
              padding: 16,
              cursor: "pointer",
              borderLeft: a.priority === "high" ? `4px solid ${COLORS.gold}` : `4px solid ${COLORS.purple}`,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 15, color: COLORS.ink }}>{a.title}</div>
            <div style={{ fontSize: 13, color: COLORS.muted, fontWeight: 600, marginTop: 6, lineHeight: 1.5 }}>
              {(a.body || "").slice(0, 140)}{(a.body || "").length > 140 ? "…" : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const UpcomingBlock = (
    <div>
      <SectionTitle eyebrow="This week">Upcoming Events</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {events.slice(0, 4).map((e) => (
          <div
            key={e.id}
            className="ls-card"
            data-testid={`home-event-${e.id}`}
            onClick={() => onOpenDetail({ title: "Events", body: "events" })}
            style={{ padding: 14, display: "flex", gap: 14, alignItems: "center", cursor: "pointer" }}
          >
            <div style={{ textAlign: "center", minWidth: 54 }}>
              <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.purple, lineHeight: 1 }}>
                {e.date_display?.split(" ")[1]}
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.muted, letterSpacing: 0.5, marginTop: 3 }}>
                {(e.date_display?.split(" ")[0] || "").toUpperCase()}
              </div>
            </div>
            <div style={{ width: 1, alignSelf: "stretch", background: COLORS.border }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14.5, color: COLORS.ink }}>{e.title}</div>
              <div style={{ fontSize: 12.5, color: COLORS.muted, fontWeight: 600, marginTop: 3 }}>{e.location}</div>
            </div>
            <Ic d={ICONS.arrow} color={COLORS.purple} />
          </div>
        ))}
        {events.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: COLORS.muted, fontSize: 13, fontWeight: 600 }}>
            No upcoming events posted yet.
          </div>
        )}
      </div>
    </div>
  );

  const NoticesBlock = community.length > 0 ? (
    <div>
      <SectionTitle eyebrow="Notices">Community Notices</SectionTitle>
      <div className="ls-notices-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {community.slice(0, 4).map((c) => (
          <button
            key={c.id}
            data-testid={`home-notice-${c.id}`}
            onClick={() => onNav("community")}
            className="ls-card no-tap-highlight"
            style={{ padding: 14, cursor: "pointer", textAlign: "left", border: "1px solid " + COLORS.border, background: "#fff" }}
          >
            <div style={{ width: 38, height: 38, borderRadius: 12, background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Ic d={ICONS[c.icon] || ICONS.info} color={COLORS.plum} size={20} />
            </div>
            <div style={{ fontWeight: 800, fontSize: 13.5, marginTop: 10, lineHeight: 1.25, color: COLORS.ink }}>{c.title}</div>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, marginTop: 4, lineHeight: 1.4 }}>
              {(c.short || "").slice(0, 70)}{(c.short || "").length > 70 ? "…" : ""}
            </div>
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const QuickAccessBlock = (
    <div>
      <SectionTitle eyebrow="Shortcuts">Quick Access</SectionTitle>
      <div className="ls-desktop-grid-quick" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {[
          ["Give", ICONS.heart, () => onNav("give"), "home-quick-give"],
          ["Times", ICONS.clock, () => onNav("times"), "home-quick-times"],
          ["Events", ICONS.cal, () => onOpenDetail({ title: "Events", body: "events" }), "home-quick-events"],
          ["Ask AI", ICONS.chat, () => onOpenAI(), "home-quick-ai"],
        ].map(([t, d, fn, tid]) => (
          <button key={t} data-testid={tid} onClick={fn} className="no-tap-highlight"
            style={{ background: "#fff", border: "1px solid " + COLORS.border, borderRadius: 16, padding: "14px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer", boxShadow: "0 4px 12px rgba(91,42,130,0.06)" }}>
            <Ic d={d} color={COLORS.purple} size={22} />
            <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.ink }}>{t}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const DonateBlock = (
    <div
      data-testid="home-donate-banner"
      onClick={() => onNav("give")}
      className="ls-card no-tap-highlight"
      style={{
        padding: 20,
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor: "pointer",
        background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldLight})`,
        border: "none",
      }}
    >
      <div style={{ width: 54, height: 54, borderRadius: 16, background: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Ic d={ICONS.heart} color="#3a2a10" size={28} w={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 19, color: "#3a2a10", lineHeight: 1.1 }}>
          Drop a coin in the tin
        </div>
        <div style={{ fontSize: 13, color: "#5a4520", fontWeight: 600, marginTop: 4, lineHeight: 1.4 }}>
          Support your community with a chai gift
        </div>
      </div>
      <Ic d={ICONS.arrow} color="#3a2a10" />
    </div>
  );

  const SponsorBlock = (
    <div>
      <SectionTitle eyebrow="Partners">Our Sponsors</SectionTitle>
      <div className="ls-card" style={{ padding: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
          {SPONSOR_PLACEHOLDERS.map((s) => (
            <div
              key={s.id}
              data-testid={`home-sponsor-${s.id}`}
              style={{
                background: `repeating-linear-gradient(45deg, ${COLORS.lilac}, ${COLORS.lilac} 8px, #fbf5ff 8px, #fbf5ff 16px)`,
                border: `1.5px dashed ${COLORS.border}`,
                borderRadius: 12,
                minHeight: 68,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "10px 8px",
              }}
            >
              <span style={{ fontSize: 11.5, fontWeight: 800, color: COLORS.purple, letterSpacing: 0.4 }}>{s.name}</span>
            </div>
          ))}
        </div>
        <button
          data-testid="home-become-sponsor-btn"
          onClick={() => { window.location.href = "mailto:admin@linkshul.co.za?subject=Become%20a%20LinkShul%20Sponsor"; }}
          className="ls-primary-btn no-tap-highlight"
          style={{ width: "100%", marginTop: 14, fontSize: 14 }}
        >
          Become a Sponsor
        </button>
        <div style={{ fontSize: 11.5, color: COLORS.muted, fontWeight: 600, marginTop: 10, textAlign: "center", lineHeight: 1.5 }}>
          Get your business seen by the LinkShul community. Apply via admin@linkshul.co.za.
        </div>
      </div>
    </div>
  );

  return (
    <div className="ls-scroll" data-testid="home-screen" style={{ paddingBottom: 100 }}>
      {/* Desktop hero (hidden on mobile) */}
      <section className="ls-desktop-hero" style={{
        background: `linear-gradient(150deg, ${COLORS.purple}, ${COLORS.plum})`, color: "#fff", padding: "56px 0 64px",
      }}>
        <div className="ls-content" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 48, alignItems: "center" }}>
          <div>
            <div style={{ color: "rgba(255,255,255,0.78)", fontWeight: 800, fontSize: 13, letterSpacing: 1.4, textTransform: "uppercase" }}>
              Shalom {member?.first_name || ""} 👋
            </div>
            <h1 className="font-heading" style={{ fontWeight: 800, fontSize: 52, lineHeight: 1.05, margin: "12px 0 18px", letterSpacing: "-1px" }}>
              Welcome to the <span style={{ color: COLORS.goldLight }}>LinkShul</span> community portal
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "rgba(255,255,255,0.86)", fontWeight: 500, maxWidth: 620, margin: 0 }}>
              Stay connected with services, learning, events and community life at LinkShul.
            </p>
            <div style={{ marginTop: 22, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.16)", padding: "8px 16px", borderRadius: 22, fontWeight: 700, fontSize: 13 }}>
                {today}<span style={{ opacity: 0.72, marginLeft: 10 }}>· {todayHebrew}</span>
              </div>
              <button
                data-testid="home-hero-ai-btn"
                onClick={() => onOpenAI()}
                className="no-tap-highlight"
                style={{ background: COLORS.gold, color: "#3a2a10", border: "none", borderRadius: 22, padding: "8px 18px", fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 8px 18px rgba(201,162,75,0.35)" }}
              >
                Ask the AI Assistant →
              </button>
            </div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", padding: 28, borderRadius: 22, border: "1px solid rgba(255,255,255,0.2)", boxShadow: "0 20px 40px rgba(0,0,0,0.18)" }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", fontWeight: 800, letterSpacing: 1.4 }}>NEXT MINYAN</div>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 36, marginTop: 8, lineHeight: 1 }}>{next?.name}</div>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 56, color: COLORS.goldLight, lineHeight: 1, marginTop: 6 }}>{next?.time}</div>
            <button
              data-testid="home-all-times-btn-desktop"
              onClick={() => onNav("times")}
              className="no-tap-highlight"
              style={{ marginTop: 18, background: "#fff", color: COLORS.purple, border: "none", borderRadius: 22, padding: "10px 20px", fontWeight: 800, fontSize: 13.5, cursor: "pointer", boxShadow: "0 6px 14px rgba(0,0,0,0.18)" }}
            >
              See all times →
            </button>
          </div>
        </div>
      </section>

      {/* Mobile-only header */}
      <div className="ls-header ls-mobile-only" style={{ padding: "16px 0 26px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 22px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ height: 40, borderRadius: 10 }} />
            <Wordmark scale={0.85} light />
          </div>
          <button data-testid="home-profile-btn" onClick={() => onNav("more")} aria-label="Profile" className="no-tap-highlight"
            style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.18)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Ic d={ICONS.user} color="#fff" size={20} />
          </button>
        </div>
        <div style={{ padding: "16px 22px 0" }}>
          <div style={{ color: "rgba(255,255,255,0.8)", fontWeight: 700, fontSize: 13 }}>
            Shalom {member?.first_name ? member.first_name + " 👋" : "👋"}
          </div>
          <div className="font-heading" style={{ color: "#fff", fontWeight: 800, fontSize: 23, lineHeight: 1.15, marginTop: 2 }}>
            Welcome to the LinkShul community
          </div>
          {isPhoneApp && <button className="ls-outline-btn" onClick={() => onOpenDetail({ title: "Welcome to LinkShul", body: "welcome" })}>Welcome to LinkShul →</button>}
          <div style={{ display: "inline-block", marginTop: 12, background: "rgba(255,255,255,0.16)", color: "#fff", padding: "6px 14px", borderRadius: 20, fontWeight: 700, fontSize: 13 }}>
            {today}<span style={{ opacity: 0.78, marginLeft: 8 }}>· {todayHebrew}</span>
          </div>
        </div>
      </div>

      <div className="ls-content ls-home-body" style={{ padding: "0 18px" }}>
        {/* Mobile-only Next Minyan card (desktop uses hero card) */}
        <div className="ls-card ls-mobile-only" data-testid="home-next-minyan" style={{ marginTop: -14, padding: 16, display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 2 }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, background: COLORS.goldLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic d={ICONS.clock} color={COLORS.plum} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 700 }}>NEXT MINYAN</div>
            <div style={{ fontWeight: 800, fontSize: 17, marginTop: 2 }}>{next ? `${next.name} · ${next.time}` : "See the service schedule"}</div>
          </div>
          <button data-testid="home-all-times-btn" onClick={() => onNav("times")} className="ls-outline-btn no-tap-highlight">All times</button>
        </div>

        {/* MOBILE-only linear flow (desktop uses the two-column grid below) */}
        <div className="ls-mobile-only">
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${isPhoneApp ? 3 : 4},1fr)`, gap: 8, marginTop: 18 }}>
            {[
              ...(isPhoneApp ? [["Weekly Newsletter", ICONS.book, () => onOpenDetail({ title: "Newsletters", body: "newsletters" }), "home-quick-newsletter"]] : [
                ["Give", ICONS.heart, () => onNav("give"), "home-quick-give-m"],
                ["Times", ICONS.clock, () => onNav("times"), "home-quick-times-m"],
              ]),
              ["Events", ICONS.cal, () => onOpenDetail({ title: "Events", body: "events" }), "home-quick-events-m"],
              [isPhoneApp ? "Ask Rabbi AI" : "Ask AI", ICONS.chat, () => onOpenAI(), "home-quick-ai-m"],
            ].map(([t, d, fn, tid]) => (
              <button key={t} data-testid={tid} onClick={fn} className="no-tap-highlight"
                style={{ background: "#fff", border: "1px solid " + COLORS.border, borderRadius: 16, padding: "12px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", boxShadow: "0 4px 12px rgba(91,42,130,0.06)" }}>
                <Ic d={d} color={COLORS.purple} size={22} />
                <span style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.ink }}>{t}</span>
              </button>
            ))}
          </div>

          {announcements.length > 0 && (
            <>
              <div className="font-heading" style={{ fontWeight: 700, fontSize: 19, color: COLORS.ink, margin: "22px 0 10px" }}>{isPhoneApp ? "Latest Announcements" : "Announcements"}</div>
              {announcements.slice(0, isPhoneApp ? 3 : 2).map((a) => (
                <div key={a.id} className="ls-card" data-testid={`home-announcement-m-${a.id}`} onClick={() => onOpenDetail({ title: "Announcements", body: "announcements" })}
                  style={{ padding: 14, marginBottom: 10, cursor: "pointer", borderLeft: a.priority === "high" ? `4px solid ${COLORS.gold}` : `4px solid ${COLORS.purple}` }}>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{a.title}</div>
                  <div style={{ fontSize: 12.5, color: COLORS.muted, fontWeight: 600, marginTop: 4, lineHeight: 1.4 }}>{(a.body || "").slice(0, 90)}…</div>
                </div>
              ))}
              {isPhoneApp && announcements.length > 3 && <button className="ls-outline-btn" onClick={() => onOpenDetail({ title: "Announcements", body: "announcements" })}>More announcements</button>}
            </>
          )}

          <div data-testid="home-donate-banner-m" onClick={() => onNav("give")} className="ls-card no-tap-highlight"
            style={{ marginTop: 18, padding: 16, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldLight})`, border: "none" }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Ic d={ICONS.heart} color="#3a2a10" size={26} w={2} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="font-heading" style={{ fontWeight: 800, fontSize: 18, color: "#3a2a10" }}>{isPhoneApp ? "Drop a coin in the Tzedakah Box" : "Drop a coin in the tin"}</div>
              <div style={{ fontSize: 13, color: "#5a4520", fontWeight: 600, marginTop: 2 }}>Support your community with a {isPhoneApp ? "Chai" : "chai"} gift</div>
            </div>
            <Ic d={ICONS.arrow} color="#3a2a10" />
          </div>

          <div className="font-heading" style={{ fontWeight: 700, fontSize: 19, color: COLORS.ink, margin: "22px 0 10px" }}>Explore</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              ["Newsletters", ICONS.book, () => onOpenDetail({ title: "Newsletters", body: "newsletters" }), "home-tile-newsletters"],
              ["Learning", ICONS.book, () => onOpenDetail({ title: "Learning", body: "learning" }), "home-tile-learning"],
              ["Directory", ICONS.people, () => onOpenDetail({ title: "Member Directory", body: "directory" }), "home-tile-directory"],
              ["Community", ICONS.grid, () => onNav("community"), "home-tile-community"],
            ].map(([t, d, fn, tid]) => (
              <button key={t} data-testid={tid} onClick={fn} className="ls-card no-tap-highlight"
                style={{ padding: 14, cursor: "pointer", textAlign: "left", border: "1px solid " + COLORS.border, background: "#fff" }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic d={d} color={COLORS.plum} size={22} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 10, lineHeight: 1.2 }}>{t}</div>
              </button>
            ))}
          </div>

          <div className="font-heading" style={{ fontWeight: 700, fontSize: 19, color: COLORS.ink, margin: "22px 0 10px" }}>Upcoming</div>
          {events.slice(0, 3).map((e) => (
            <div key={e.id} className="ls-card" data-testid={`home-event-m-${e.id}`} style={{ padding: 14, marginBottom: 10, display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ textAlign: "center", minWidth: 46 }}>
                <div className="font-heading" style={{ fontWeight: 800, fontSize: 19, color: COLORS.purple }}>{e.date_display?.split(" ")[1]}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted }}>{e.date_display?.split(" ")[0]}</div>
              </div>
              <div style={{ width: 1, alignSelf: "stretch", background: COLORS.border }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{e.title}</div>
                <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, marginTop: 2 }}>{e.location}</div>
              </div>
            </div>
          ))}
          {isPhoneApp && events.length > 3 && <button className="ls-outline-btn" onClick={() => onOpenDetail({ title: "Events", body: "events" })}>More events</button>}
          {isPhoneApp && <div style={{ marginTop: 22 }}><a className="ls-outline-btn" href="mailto:admin@linkshul.co.za?subject=Become%20a%20LinkShul%20Sponsor">Become a Sponsor</a></div>}
        </div>

        {/* DESKTOP-only two-column layout */}
        <div className="ls-desktop-only ls-home-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 40, marginTop: 40 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {AnnouncementsBlock}
            {UpcomingBlock}
            {NoticesBlock}
          </div>
          <aside style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {QuickAccessBlock}
            <div style={{ marginTop: 26 }}>
              <SectionTitle eyebrow="Support">Give to LinkShul</SectionTitle>
              {DonateBlock}
            </div>
            {SponsorBlock}
          </aside>
        </div>
      </div>
    </div>
  );
}
